import * as angular from 'angular';
import {ILogService} from 'angular';
import {AccountModel, SchedulingProjectConfigurations} from "../angular/models/account.model";
import {AccountRequests} from "./account-requests.service";
import {AccountsAlerts} from "./accounts-alerts.service";
import {BringozLoaderService} from "../../commons/services/bringoz-loader.service";
import {ExportAccountsPayloadModel} from "../models/export-accounts-payload.model";
import {AgentsPaginationQueryModel} from "../../agents/angular/models/agents-pagination-query.model";
import {MetaDataModel} from "../../commons/models/meta-data.model";

export class AccountsService {

  constructor(private accountRequests: AccountRequests, private accountsAlerts: AccountsAlerts,
              private bringozLoaderService: BringozLoaderService, private $log: ILogService, private $q) {
  }

  createOne(account: AccountModel): Promise<AccountModel> {
    return new Promise((resolve, reject) => {
      this.bringozLoaderService.on();
      this.accountRequests.createOne(account)
        .then((createdAccount) => {
          this.accountsAlerts.demandCreateSuccess();
          resolve(createdAccount);
        })
        .catch((error) => {
          this.$log.error(error);
          this.accountsAlerts.demandCreateError(error);
          reject(error);
        })
        .finally(() => {
          this.bringozLoaderService.off();
        });
    });
  }

  updateOne(accountId: string, account: AccountModel): Promise<AccountModel> {
    return new Promise((resolve, reject) => {
      this.bringozLoaderService.on();
      this.accountRequests.updateOne(accountId, account)
        .then((updatedAccount) => {
          this.accountsAlerts.demandUpdateSuccess();
          resolve(updatedAccount);
        })
        .catch((error) => {
          this.$log.error(error);
          this.accountsAlerts.demandUpdateError();
          reject(error);
        })
        .finally(() => {
          this.bringozLoaderService.off();
        });
    });
  }

  addDepotToAccounts(depotId: string, accountIds: string[], excludeIds: string[]): Promise<AccountModel[]> {
    return new Promise((resolve, reject) => {
      this.bringozLoaderService.on();
      this.accountRequests.addDepotToAccounts(depotId, accountIds, excludeIds)
        .then((res) => {
          this.accountsAlerts.demandAddToDepotSuccess();
          resolve(res);
        })
        .catch((error) => {
          this.$log.error(error);
          this.accountsAlerts.demandAddToDepotError();
          reject(error);
        })
        .finally(() => {
          this.bringozLoaderService.off();
        });
    });
  }


  exportAccounts(payload: ExportAccountsPayloadModel, branchIds, accountType: string, tags?: string[], isActive?: boolean): Promise<AccountModel> {
    return new Promise((resolve, reject) => {
      this.bringozLoaderService.on();
      this.accountRequests.exportAccounts(payload, branchIds, accountType, tags, isActive)
        .then((updatedAgent) => {
          this.accountsAlerts.exportSuccess();
          resolve(updatedAgent);
        })
        .catch((error) => {
          this.$log.error(error);
          this.accountsAlerts.exportError();
          reject(error);
        })
        .finally(() => {
          this.bringozLoaderService.off();
        });
    });
  }

  removeContract(accountId: string): Promise<AccountModel> {
    return new Promise((resolve, reject) => {
      this.bringozLoaderService.on();
      this.accountRequests.removeContract(accountId)
        .then((account) => {
          this.accountsAlerts.contractRemovedSuccess();
          resolve(account);
        })
        .catch((error) => {
          this.$log.error(error);
          this.accountsAlerts.contractRemovedError();
          reject(error);
        })
        .finally(() => {
          this.bringozLoaderService.off();
        });
    });
  }

  unArchive(accountId: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.bringozLoaderService.on();
      this.accountRequests.unArchive(accountId)
        .then((account) => {
          this.accountsAlerts.changeArchiveStatusSuccess();
          resolve(account);
        })
        .catch((error) => {
          this.$log.error(error);
          this.accountsAlerts.changeArchiveStatusError();
          reject(error);
        })
        .finally(() => {
          this.bringozLoaderService.off();
        });
    });
  }

  addBranches(accountIds: string[], branchIds: string[]): Promise<AccountModel[]> {
    return new Promise((resolve, reject) => {
      this.bringozLoaderService.on();
      this.accountRequests.addBranches(accountIds, branchIds)
        .then((updatedAccounts: AccountModel[]) => {
          this.accountsAlerts.exportSuccess();
          resolve(updatedAccounts);
        })
        .catch((error) => {
          this.$log.error(error);
          this.accountsAlerts.exportError();
          reject(error);
        })
        .finally(() => {
          this.bringozLoaderService.off();
        });
    });
  }

  getAll(queryParams) {
    const deferred = this.$q.defer();

    this.bringozLoaderService.on();
    this.accountRequests.getAll(queryParams)
      .then((res) => {
        deferred.resolve(res);
      })
      .catch((error) => {
        this.$log.error(error);
        deferred.reject(error)
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });

    return deferred.promise
  }


  setSchedulingProjectConfigurations(accountId: string, payload: SchedulingProjectConfigurations) {
    const deferred = this.$q.defer();

    this.bringozLoaderService.on();
    this.accountRequests.setSchedulingProjectConfigurations(accountId, payload)
      .then((res) => {
        this.accountsAlerts.updateAccountSchedulingProjectConfigurationsSuccess();
        deferred.resolve(res);
      })
      .catch((error) => {
        this.accountsAlerts.updateAccountSchedulingProjectConfigurationsError();
        this.$log.error(error);
        deferred.reject(error);
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });

    return deferred.promise
  }

  getPreferredAgents(accountId: string, agentData: any) {
    const deferred = this.$q.defer();

    this.bringozLoaderService.on();
    this.accountRequests.getPreferredAgents(accountId, agentData)
      .then((res) => {
        deferred.resolve(res);
      })
      .catch((error) => {
        this.accountsAlerts.getRestrictedAgentsError();
        this.$log.error(error);
        deferred.reject(error);
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });

    return deferred.promise;
  }

  createPreferredAgent(accountId: string, agentData: any) {
    const deferred = this.$q.defer();

    this.bringozLoaderService.on();
    this.accountRequests.createPreferredAgent(accountId, agentData)
      .then((res) => {
        this.accountsAlerts.addPreferredAgentSuccess();
        deferred.resolve(res);
      })
      .catch((error) => {
        this.accountsAlerts.addPreferredAgentError();
        this.$log.error(error);
        deferred.reject(error);
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });

    return deferred.promise
  }

  updatePreferredAgents(accountId: string, agents: any[]) {
    const deferred = this.$q.defer();

    this.bringozLoaderService.on();
    this.accountRequests.updatePreferredAgents(accountId, agents)
      .then((res) => {
        this.accountsAlerts.updatePreferredAgentSuccess();
        deferred.resolve(res);
      })
      .catch((error) => {
        this.accountsAlerts.updatePreferredAgentError();
        this.$log.error(error);
        deferred.reject(error);
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });

    return deferred.promise
  }

  deletePreferredAgents(accountId: string, agentIds: string[]) {
    const deferred = this.$q.defer();

    this.bringozLoaderService.on();
    this.accountRequests.deletePreferredAgents(accountId, agentIds)
      .then((res) => {
        this.accountsAlerts.removePreferredAgentSuccess();
        deferred.resolve(res);
      })
      .catch((error) => {
        this.accountsAlerts.removePreferredAgentError();
        this.$log.error(error);
        deferred.reject(error);
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });

    return deferred.promise
  }



  getRestrictedAgents(accountId: string, agentData: any) {
    const deferred = this.$q.defer();

    this.bringozLoaderService.on();
    this.accountRequests.getRestrictedAgents(accountId, agentData)
      .then((res) => {
        deferred.resolve(res);
      })
      .catch((error) => {
        this.accountsAlerts.getRestrictedAgentsError();
        this.$log.error(error);
        deferred.reject(error);
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });

    return deferred.promise
  }

  createRestrictedAgent(accountId: string, agentData: any) {
    const deferred = this.$q.defer();

    this.bringozLoaderService.on();
    this.accountRequests.createRestrictedAgent(accountId, agentData)
      .then((res) => {
        this.accountsAlerts.addRestrictedAgentSuccess();
        deferred.resolve(res);
      })
      .catch((error) => {
        this.accountsAlerts.addRestrictedAgentError();
        this.$log.error(error);
        deferred.reject(error);
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });

    return deferred.promise
  }

  updateRestrictedAgents(accountId: string, agents: any[]) {
    const deferred = this.$q.defer();

    this.bringozLoaderService.on();
    this.accountRequests.updateRestrictedAgents(accountId, agents)
      .then((res) => {
        this.accountsAlerts.updateRestrictedAgentSuccess();
        deferred.resolve(res);
      })
      .catch((error) => {
        this.accountsAlerts.updateRestrictedAgentError();
        this.$log.error(error);
        deferred.reject(error);
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });

    return deferred.promise
  }

  deleteRestrictedAgents(accountId: string, agentIds: string[]) {
    const deferred = this.$q.defer();

    this.bringozLoaderService.on();
    this.accountRequests.deleteRestrictedAgents(accountId, agentIds)
      .then((res) => {
        this.accountsAlerts.removeRestrictedAgentSuccess();
        deferred.resolve(res);
      })
      .catch((error) => {
        this.accountsAlerts.removeRestrictedAgentError();
        this.$log.error(error);
        deferred.reject(error);
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });

    return deferred.promise
  }


  getDedicatedAgents(accountId: string, agentData: any) {
    const deferred = this.$q.defer();

    this.bringozLoaderService.on();
    this.accountRequests.getDedicatedAgents(accountId, agentData)
      .then((res) => {
        deferred.resolve(res);
      })
      .catch((error) => {
        this.accountsAlerts.getRestrictedAgentsError();
        this.$log.error(error);
        deferred.reject(error);
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });

    return deferred.promise
  }

  createDedicatedAgent(accountId: string, agentData: any) {
    const deferred = this.$q.defer();

    this.bringozLoaderService.on();
    this.accountRequests.createDedicatedAgent(accountId, agentData)
      .then((res) => {
        this.accountsAlerts.addRestrictedAgentSuccess();
        deferred.resolve(res);
      })
      .catch((error) => {
        this.accountsAlerts.addRestrictedAgentError();
        this.$log.error(error);
        deferred.reject(error);
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });

    return deferred.promise
  }

  updateDedicatedAgents(accountId: string, agents: any[]) {
    const deferred = this.$q.defer();

    this.bringozLoaderService.on();
    this.accountRequests.updateDedicatedAgents(accountId, agents)
      .then((res) => {
        this.accountsAlerts.updateRestrictedAgentSuccess();
        deferred.resolve(res);
      })
      .catch((error) => {
        this.accountsAlerts.updateRestrictedAgentError();
        this.$log.error(error);
        deferred.reject(error);
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });

    return deferred.promise
  }

  deleteDedicatedAgents(accountId: string, agentIds: string[]) {
    const deferred = this.$q.defer();

    this.bringozLoaderService.on();
    this.accountRequests.deleteDedicatedAgents(accountId, agentIds)
      .then((res) => {
        this.accountsAlerts.removeRestrictedAgentSuccess();
        deferred.resolve(res);
      })
      .catch((error) => {
        this.accountsAlerts.removeRestrictedAgentError();
        this.$log.error(error);
        deferred.reject(error);
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });

    return deferred.promise
  }

  getAccountsWithPreferredAgents(params: any, useCursorEndpoint?: boolean) {
    const deferred = this.$q.defer();

    this.bringozLoaderService.on();
    this.accountRequests.getAccountsWithPreferredAgents(params, useCursorEndpoint)
      .then((res) => {
        deferred.resolve(res);
      })
      .catch((error) => {
        this.accountsAlerts.getAccountsWithPreferredAgentsError();
        this.$log.error(error);
        deferred.reject(error);
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });

    return deferred.promise;
  }

  assignMultiplePreferredAgents(accountId: string, payload: any) {
    const deferred = this.$q.defer();

    this.bringozLoaderService.on();
    this.accountRequests.assignMultiplePreferredAgents(accountId, payload)
      .then((res) => {
        this.accountsAlerts.assignMultiplePreferredAgentsSuccess();
        deferred.resolve(res);
      })
      .catch((error) => {
        this.accountsAlerts.assignMultiplePreferredAgentsError();
        this.$log.error(error);
        deferred.reject(error);
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });

    return deferred.promise;
  }

  updateMetaData(accountId: string, metaData: MetaDataModel[]) {
    const deferred = this.$q.defer();

    this.bringozLoaderService.on();
    this.accountRequests.updateMetaData(accountId, metaData)
      .then((res) => {
        this.accountsAlerts.removeRestrictedAgentSuccess();
        deferred.resolve(res);
      })
      .catch((error) => {
        this.accountsAlerts.removeRestrictedAgentError();
        this.$log.error(error);
        deferred.reject(error);
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });

    return deferred.promise
  }

}

angular
  .module('bringoz.admin.accounts')
  .service('accountsService', AccountsService);
